// A "session" is one continuous stretch of app use on one device — that is what
// the adoption heatmap counts. sessionStorage (not localStorage) plus an idle
// rotation, so a shared outlet tablet left on /cu/today does not read as a
// single session forever.

export const SESSION_IDLE_MS = 30 * 60 * 1000;

function keys(prefix: string): { id: string; seen: string } {
  return {
    id: `${prefix}_usage_session_id`,
    seen: `${prefix}_usage_session_seen`,
  };
}

/**
 * crypto.randomUUID() is SECURE-CONTEXT ONLY. The repo's phone-testing lane is
 * a LAN dev server on plain http://192.168.x.x — there it is `undefined`, and
 * an unguarded call inside a layout-mounted effect would throw straight into
 * app/cu/error.tsx and take down the whole /cu tree for a telemetry beacon.
 * Same reasoning for sessionStorage, which throws in locked-down privacy modes.
 */
export function randomId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // RFC-4122-shaped fallback. Uniqueness is all this needs — it is a grouping
  // key, never a security token.
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => {
    const n = Number(c);
    return (n ^ (Math.floor(Math.random() * 256) & (15 >> (n / 4)))).toString(16);
  });
}

export function resolveUsageSession(now: number, prefix = "cu"): string {
  try {
    const { id: idKey, seen: seenKey } = keys(prefix);
    const lastSeen = Number(sessionStorage.getItem(seenKey) ?? "0");
    const existing = sessionStorage.getItem(idKey);
    const fresh = existing && now - lastSeen <= SESSION_IDLE_MS;
    const id = fresh ? existing : randomId();
    sessionStorage.setItem(idKey, id);
    sessionStorage.setItem(seenKey, String(now));
    return id;
  } catch {
    // Storage unavailable — every beacon becomes its own session. Adoption
    // counts inflate slightly; nothing breaks.
    return randomId();
  }
}
